import { useMemo, useState } from 'react';
import { Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { FormHeader, HeaderItem } from '../FormHeader';
import { BackButton } from 'components';
import { BAAPannel, LoginPannel, SecurityPannel } from './components';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './AccountSettings.module.scss';

type ProfileSettingsProps = {
  onBackToSettingsClick(): void;
};

type Tab = 'Login' | 'Security' | 'BAA';

export function AccountSettings({ onBackToSettingsClick }: ProfileSettingsProps) {
  const { t } = useTranslation('settings');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [tab, setTab] = useState<Tab | null>(isMobile ? null : 'Login');

  const headerItems: HeaderItem[] = useMemo(
    () =>
      [
        {
          text: t('accountSettings.loginUpper'),
          isSelected: tab === 'Login',
          onClick: () => setTab('Login'),
        },
        {
          text: t('accountSettings.securityUpper'),
          isSelected: tab === 'Security',
          onClick: () => setTab('Security'),
        },
        isHipaaLink && {
          text: t('accountSettings.baa'),
          isSelected: tab === 'BAA',
          onClick: () => setTab('BAA'),
        },
      ].filter(Boolean) as HeaderItem[],
    [tab, t],
  );

  const getMobileHeaderName = (): string => {
    switch (tab) {
      case 'Login':
        return t('accountSettings.loginUpper');
      case 'Security':
        return t('accountSettings.securityUpper');
      case 'BAA':
        return t('accountSettings.baa');
      default:
        return t('accountSettings.accountSettingsUpper');
    }
  };

  const handleMyProfileClick = (): void => {
    setTab('Login');
  };

  const handleRoomSettingsClick = (): void => {
    setTab('Security');
  };

  const handleBAAClick = (): void => {
    setTab('BAA');
  };

  const handleBackToProfile = (): void => {
    setTab(null);
  };

  return (
    <>
      {!tab && isMobile && <BackButton text={t('accountSettings.backToSettings')} onClick={onBackToSettingsClick} />}
      {!!tab && isMobile && (
        <BackButton text={t('accountSettings.backToAccountSettings')} onClick={handleBackToProfile} />
      )}

      <div className={styles.container}>
        {isMobile && (
          <>
            <FormHeader items={[{ text: getMobileHeaderName() }]} />

            {!tab && (
              <>
                <Stack className={styles.navigationItem} onClick={handleMyProfileClick}>
                  <Typography variant='body2'>{t('accountSettings.login')}</Typography>
                  <ChevronRight />
                </Stack>
                <Stack className={styles.navigationItem} onClick={handleRoomSettingsClick}>
                  <Typography variant='body2'>{t('accountSettings.security')}</Typography>
                  <ChevronRight />
                </Stack>
                {isHipaaLink && (
                  <Stack className={styles.navigationItem} onClick={handleBAAClick}>
                    <Typography variant='body2'>{t('accountSettings.baa')}</Typography>
                    <ChevronRight />
                  </Stack>
                )}
              </>
            )}

            {tab && (
              <div className={styles.main}>
                {tab === 'Login' && <LoginPannel />}
                {tab === 'Security' && <SecurityPannel />}
                {isHipaaLink && tab === 'BAA' && <BAAPannel />}
              </div>
            )}
          </>
        )}

        {!isMobile && (
          <>
            <FormHeader items={headerItems} />
            {tab === 'Login' && <LoginPannel />}
            {tab === 'Security' && <SecurityPannel />}
            {isHipaaLink && tab === 'BAA' && <BAAPannel />}
          </>
        )}
      </div>
    </>
  );
}
